<!-- Launcher button — shown only when panel is closed -->
@if (!isOpen) {
  <button class="ai-launcher" (click)="toggle()" title="Open AI Assistant" aria-label="Open AI Assistant">
    <mat-icon class="ai-launcher__icon">auto_awesome</mat-icon>
  </button>
}

<!-- Floating panel — shown when open (collapsed = header only, expanded = full chat) -->
@if (isOpen) {
  <div class="ai-widget" [class.ai-widget--collapsed]="isCollapsed">

    <!-- Header bar — always visible when open -->
    <div class="ai-widget__header" (click)="isCollapsed = !isCollapsed">
      <div class="ai-widget__header-title">
        <mat-icon class="ai-widget__header-icon">auto_awesome</mat-icon>
        <span>AI Assistant</span>
      </div>
      <div class="ai-widget__header-actions">
        <button
          class="ai-widget__header-btn"
          (click)="$event.stopPropagation(); isCollapsed = !isCollapsed"
          [title]="isCollapsed ? 'Expand' : 'Minimise'"
        >
          <mat-icon>{{ isCollapsed ? 'expand_less' : 'remove' }}</mat-icon>
        </button>
        <button
          class="ai-widget__header-btn"
          (click)="$event.stopPropagation(); close()"
          title="Close"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Body — hidden when collapsed -->
    @if (!isCollapsed) {
      <div class="ai-widget__body">

        <!-- Chat messages area -->
        <div class="chat-container" #chatContainer>

          <!-- Welcome state -->
          @if (messages.length === 0 && !loading) {
            <div class="welcome">
              <div class="welcome-title">AI Assistant</div>
              <div class="welcome-subtitle">Ask questions about your data — reviews, reservations, properties, and more.</div>
              <div class="suggestions">
                @for (q of suggestedQuestions; track q) {
                  <button class="suggestion-chip" (click)="useSuggestion(q)">{{ q }}</button>
                }
              </div>
            </div>
          }

          <!-- Messages -->
          @for (msg of messages; track msg) {
            <div class="message" [ngClass]="msg.role">

              <!-- User message -->
              @if (msg.role === 'user') {
                <div class="bubble user-bubble">{{ msg.text }}</div>
              }

              <!-- Assistant message -->
              @if (msg.role === 'assistant') {
                <div class="bubble assistant-bubble">

                  <!-- Domain/intent badges -->
                  @if (msg.domain || msg.intent) {
                    <div class="badges">
                      @if (msg.domain) {
                        <span class="badge domain">{{ msg.domain }}</span>
                      }
                      @if (msg.intent) {
                        <span class="badge intent">{{ msg.intent }}</span>
                      }
                    </div>
                  }

                  <!-- Write confirmation -->
                  @if (msg.pendingConfirmation) {
                    <div class="write-confirm">
                      <div class="confirm-title">This action will modify data:</div>
                      <div class="confirm-intent">{{ msg.intent }} on {{ msg.domain }}</div>
                      @if (getPlanSteps(msg).length > 0) {
                        <div class="plan-steps">
                          @for (step of getPlanSteps(msg); track step) {
                            <div class="plan-step">
                              <span class="step-method" [ngClass]="step.method?.toLowerCase()">{{ step.method }}</span>
                              <span class="step-path">{{ step.path }}</span>
                              @if (step.query) {
                                <div class="step-params">{{ pretty(step.query) }}</div>
                              }
                            </div>
                          }
                        </div>
                      }
                      <div class="confirm-actions">
                        <button pButton type="button" label="Confirm" icon="pi pi-check"
                          class="p-button-sm p-button-success" (click)="confirmWrite(msg)"></button>
                        <button pButton type="button" label="Cancel" icon="pi pi-times"
                          class="p-button-sm p-button-secondary" (click)="cancelWrite(msg)"></button>
                      </div>
                    </div>
                  }

                  <!-- Error -->
                  @if (msg.error) {
                    <div class="error-text">{{ msg.error }}</div>
                  }

                  <!-- Answer text -->
                  @if (getAnswerText(msg)) {
                    <div class="answer-text">{{ getAnswerText(msg) }}</div>
                  }

                  <!-- Count result -->
                  @if (getSummaryCount(msg) !== null) {
                    <div class="count-result">
                      <span class="count-value">{{ getSummaryCount(msg) | number }}</span>
                    </div>
                  }

                  <!-- List items -->
                  @if (getListItems(msg).length > 0) {
                    <div class="items">
                      <div class="items-header">
                        {{ getListItems(msg).length }} result{{ getListItems(msg).length !== 1 ? 's' : '' }}
                        @if (getSummaryCount(msg) !== null && getSummaryCount(msg)! > getListItems(msg).length) {
                          <span> of {{ getSummaryCount(msg) | number }} total</span>
                        }
                      </div>
                      @for (item of getListItems(msg); track item; let i = $index) {
                        <div class="item">
                          <div class="item-title">#{{ i + 1 }} — {{ getItemTitle(item) }}</div>
                          @if (getItemMeta(item).length > 0) {
                            <div class="item-meta">
                              @for (m of getItemMeta(item); track m) {
                                <span class="meta-tag">{{ m }}</span>
                              }
                            </div>
                          }
                          @if (getItemDescription(item)) {
                            <div class="item-body">{{ getItemDescription(item) }}</div>
                          }
                          @if (getItemAuthor(item)) {
                            <div class="item-footer">— {{ getItemAuthor(item) }}</div>
                          }
                        </div>
                      }
                    </div>
                  }

                  <!-- Export buttons -->
                  @if (hasListItems(msg)) {
                    <div class="export-buttons">
                      <button pButton type="button" label="CSV" icon="pi pi-download"
                        class="p-button-text p-button-sm" (click)="downloadCsv(msg)"></button>
                      <button pButton type="button" label="Excel" icon="pi pi-file-excel"
                        class="p-button-text p-button-sm" (click)="downloadExcel(msg)"></button>
                      <button pButton type="button" label="Export All" icon="pi pi-cloud-download"
                        class="p-button-text p-button-sm" (click)="exportFullDataset(msg)"
                        [disabled]="loading" pTooltip="Download full dataset as Excel"></button>
                    </div>
                  }

                  <!-- Raw JSON toggle -->
                  @if (msg.response) {
                    <div class="raw-toggle">
                      <a class="raw-link" (click)="toggleRaw(msg)">{{ msg.showRaw ? 'Hide' : 'Show' }} raw JSON</a>
                    </div>
                  }
                  @if (msg.showRaw) {
                    <pre class="raw">{{ pretty(msg.response) }}</pre>
                  }

                  <div class="msg-time">{{ msg.timestamp | date:'shortTime' }}</div>
                </div>
              }
            </div>
          }

          <!-- Loading indicator -->
          @if (loading) {
            <div class="message assistant">
              <div class="bubble assistant-bubble typing">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          }
        </div>

        <!-- Input area -->
        <div class="input-area">
          <form [formGroup]="form" (ngSubmit)="ask()" class="input-row">
            <textarea
              #questionInput
              pInputTextarea
              formControlName="question"
              [autoResize]="true"
              rows="1"
              placeholder="Ask a question... (Enter to send, Shift+Enter for new line)"
              (keydown)="onKeyDown($event)"
              [disabled]="loading"
              class="chat-input"
            ></textarea>
            <button pButton type="submit" icon="pi pi-send"
              [disabled]="loading || form.invalid"
              class="send-btn p-button-rounded"></button>
          </form>
          <div class="input-footer">
            @if (messages.length > 0) {
              <button pButton type="button" label="Clear" icon="pi pi-trash"
                class="p-button-text p-button-sm p-button-secondary"
                (click)="clearChat()"></button>
            }
          </div>
        </div>

      </div>
    }

  </div>
}
